<template>
  <div class="wf-config">
    <el-card shadow="never" class="wf-config-card">
      <div slot="header" class="wf-config-head">
        <span class="wf-config-title">申请配置</span>
        <span class="wf-config-tip">配置「发起申请」页流程的可见权限与显示图标（仅系统管理员）</span>
      </div>

      <el-tabs v-model="activeTab" @tab-click="onTabClick">
        <!-- ==================== 权限配置 ==================== -->
        <el-tab-pane label="权限配置" name="grant">
          <el-alert type="info" :closable="false" show-icon class="wf-alert"
                    title="未配置可见主体的流程，普通员工在「发起申请」页不可见（默认仅管理员可见）。" />

          <div class="wf-grant-form">
            <div class="wf-grant-col">
              <div class="wf-col-title"><span class="wf-step">1</span>选择授权对象</div>
              <el-radio-group v-model="grant.targetType" size="small" class="wf-radio" @change="onTargetTypeChange">
                <el-radio-button :label="1">一级分类</el-radio-button>
                <el-radio-button :label="2">二级分类</el-radio-button>
                <el-radio-button :label="3">具体流程</el-radio-button>
              </el-radio-group>
              <el-select v-model="grant.targetValue" size="small" filterable clearable
                         placeholder="请选择授权对象" class="wf-select">
                <template v-if="grant.targetType === 1">
                  <el-option v-for="c in targets.categories" :key="c" :label="c" :value="c" />
                </template>
                <template v-else-if="grant.targetType === 2">
                  <el-option v-for="s in targets.subCategories" :key="s" :label="s" :value="s" />
                </template>
                <template v-else>
                  <el-option v-for="f in targets.flows" :key="f.id" :label="f.flowName" :value="f.flowKey" />
                </template>
              </el-select>
            </div>

            <div class="wf-grant-col">
              <div class="wf-col-title"><span class="wf-step">2</span>选择授权主体（可多选）</div>
              <el-radio-group v-model="grant.subjectType" size="small" class="wf-radio" @change="grant.subjectValues = []">
                <el-radio-button :label="1">中心</el-radio-button>
                <el-radio-button :label="2">部门</el-radio-button>
                <el-radio-button :label="3">员工</el-radio-button>
                <el-radio-button :label="4">角色</el-radio-button>
              </el-radio-group>
              <el-select v-model="grant.subjectValues" size="small" multiple filterable collapse-tags
                         placeholder="请选择授权主体" class="wf-select">
                <template v-if="grant.subjectType === 1">
                  <el-option v-for="c in orgTree.centers" :key="c.id" :label="c.name" :value="String(c.id)" />
                </template>
                <template v-else-if="grant.subjectType === 2">
                  <el-option-group v-for="g in deptGroups" :key="g.centerId" :label="g.centerName">
                    <el-option v-for="d in g.departments" :key="d.id" :label="d.name" :value="String(d.id)" />
                  </el-option-group>
                </template>
                <template v-else-if="grant.subjectType === 3">
                  <el-option v-for="u in users" :key="u.id" :label="u.trueName || u.userName" :value="String(u.id)" />
                </template>
                <template v-else>
                  <el-option v-for="r in roles" :key="r.id" :label="r.roleName" :value="String(r.id)" />
                </template>
              </el-select>
            </div>
          </div>

          <div class="wf-grant-actions">
            <el-button type="primary" size="small" icon="el-icon-check" @click="doSaveGrant">保存授权</el-button>
            <span class="wf-grant-hint">将所选「授权主体」授权给所选「授权对象」</span>
          </div>

          <el-divider content-position="left">已授权列表</el-divider>
          <div class="wf-list-toolbar">
            <!-- 授权对象：类型 + 具体对象 -->
            <el-select v-model="listFilter.targetType" size="small" clearable placeholder="全部对象类型"
                       style="width: 150px" @change="onListTargetTypeChange">
              <el-option label="一级分类" :value="1" />
              <el-option label="二级分类" :value="2" />
              <el-option label="具体流程" :value="3" />
            </el-select>
            <el-select v-model="listFilter.targetValue" size="small" clearable filterable
                       placeholder="全部具体对象" style="width: 180px" @change="loadGrants">
              <template v-if="listFilter.targetType === 1">
                <el-option v-for="c in targets.categories" :key="c" :label="c" :value="c" />
              </template>
              <template v-else-if="listFilter.targetType === 2">
                <el-option v-for="s in targets.subCategories" :key="s" :label="s" :value="s" />
              </template>
              <template v-else-if="listFilter.targetType === 3">
                <el-option v-for="f in targets.flows" :key="f.id" :label="f.flowName" :value="f.flowKey" />
              </template>
            </el-select>

            <!-- 授权主体：类型 + 具体主体 -->
            <el-select v-model="listFilter.subjectType" size="small" clearable placeholder="全部主体类型"
                       style="width: 150px" @change="onListSubjectTypeChange">
              <el-option label="中心" :value="1" />
              <el-option label="部门" :value="2" />
              <el-option label="员工" :value="3" />
              <el-option label="角色" :value="4" />
            </el-select>
            <el-select v-model="listFilter.subjectValue" size="small" clearable filterable
                       placeholder="全部具体主体" style="width: 180px" @change="loadGrants">
              <template v-if="listFilter.subjectType === 1">
                <el-option v-for="c in orgTree.centers" :key="c.id" :label="c.name" :value="String(c.id)" />
              </template>
              <template v-else-if="listFilter.subjectType === 2">
                <el-option-group v-for="g in deptGroups" :key="g.centerId" :label="g.centerName">
                  <el-option v-for="d in g.departments" :key="d.id" :label="d.name" :value="String(d.id)" />
                </el-option-group>
              </template>
              <template v-else-if="listFilter.subjectType === 3">
                <el-option v-for="u in users" :key="u.id" :label="u.trueName || u.userName" :value="String(u.id)" />
              </template>
              <template v-else-if="listFilter.subjectType === 4">
                <el-option v-for="r in roles" :key="r.id" :label="r.roleName" :value="String(r.id)" />
              </template>
            </el-select>

            <el-button size="small" icon="el-icon-refresh" @click="loadGrants">刷新</el-button>
          </div>
          <el-table :data="grants" size="small" border v-loading="grantLoading" style="width: 100%">
            <el-table-column label="授权对象" min-width="180">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="targetTagType(row.targetType)">{{ targetTypeText(row.targetType) }}</el-tag>
                <span class="wf-cell-name">{{ row.targetName || row.targetValue }}</span>
              </template>
            </el-table-column>
            <el-table-column label="授权主体" min-width="180">
              <template slot-scope="{ row }">
                <el-tag size="mini" type="info" effect="plain">{{ subjectTypeText(row.subjectType) }}</el-tag>
                <span class="wf-cell-name">{{ row.subjectName || row.subjectValue }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90" align="center">
              <template slot-scope="{ row }">
                <el-button type="text" size="mini" @click="doDeleteGrant(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- ==================== 图标配置 ==================== -->
        <el-tab-pane label="图标配置" name="icon">
          <el-alert type="info" :closable="false" show-icon class="wf-alert"
                    title="上传 .png 替换「发起申请」页对应流程的图标；未设置时使用默认图标。" />
          <div class="wf-icon-search">
            <el-input v-model="iconKeyword" size="small" clearable prefix-icon="el-icon-search"
                      placeholder="搜索流程名称" style="width: 240px" />
          </div>
          <div class="wf-icon-grid">
            <div v-for="f in iconFlows" :key="f.id" class="wf-icon-card">
              <div class="wf-icon-preview">
                <img v-if="f.iconUrl" :src="iconUrlOf(f)" class="wf-icon-img" alt="icon" />
                <span v-else class="wf-icon-ph"
                      :style="{ color: pickIcon(f.category, f.flowName).color, background: pickIcon(f.category, f.flowName).bg }">
                  <i :class="pickIcon(f.category, f.flowName).icon"></i>
                </span>
              </div>
              <div class="wf-icon-name" :title="f.flowName">{{ f.flowName }}</div>
              <div class="wf-icon-cat">{{ f.category }}<template v-if="f.subCategory"> / {{ f.subCategory }}</template></div>
              <div class="wf-icon-ops">
                <el-upload action="" :show-file-list="false" accept=".png"
                           :before-upload="beforeIconUpload"
                           :http-request="(opt) => customIconUpload(opt, f)">
                  <el-button type="text" size="mini" icon="el-icon-upload2">更换图标</el-button>
                </el-upload>
                <el-button v-if="f.iconUrl" type="text" size="mini" icon="el-icon-refresh-left"
                           @click="resetIcon(f)">恢复默认</el-button>
              </div>
            </div>
            <div v-if="!iconFlows.length" class="wf-icon-empty">暂无已发布流程</div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import request from '@/utils/request3'
import { listGrants, saveGrants, deleteGrant, grantTargets, saveFlowIcon, orgContext, grantOrgTree } from '@/api/wflow/grant'
import { getUserList } from '@/api/plat/PlatStaff'
import { getTRoleSelectByPage } from '@/api/plat/PosMana'
import { pickIcon, fullIconUrl } from './components/flowIcon'

export default {
  name: 'WflowApplyConfig',
  data() {
    return {
      activeTab: 'grant',
      isAdmin: false,
      baseApi: this.$store.state.IPAss.TP,
      uploadAction: this.$store.state.IPAss.TP + '/work/file-manage/upload',
      targets: { categories: [], subCategories: [], flows: [] },
      orgTree: { centers: [], departments: [] },
      users: [],
      roles: [],
      grant: { targetType: 1, targetValue: '', subjectType: 1, subjectValues: [] },
      grants: [],
      grantLoading: false,
      listFilter: { targetType: null, targetValue: '', subjectType: null, subjectValue: '' },
      iconKeyword: ''
    }
  },
  computed: {
    // 部门按所属中心分组（用于部门选择下拉）
    deptGroups() {
      const centers = this.orgTree.centers || []
      const depts = this.orgTree.departments || []
      const groups = centers.map(c => ({
        centerId: c.id,
        centerName: c.name,
        departments: depts.filter(d => String(d.parentId) === String(c.id))
      }))
      const known = new Set(centers.map(c => String(c.id)))
      const orphan = depts.filter(d => !known.has(String(d.parentId)))
      if (orphan.length) groups.push({ centerId: '__other__', centerName: '其它', departments: orphan })
      return groups.filter(g => g.departments.length)
    },
    iconFlows() {
      const kw = (this.iconKeyword || '').trim().toLowerCase()
      const flows = this.targets.flows || []
      if (!kw) return flows
      return flows.filter(f => (f.flowName || '').toLowerCase().indexOf(kw) > -1)
    }
  },
  created() {
    this.init()
  },
  methods: {
    pickIcon,
    init() {
      orgContext().then(res => {
        if (res && res.code === 200) {
          this.isAdmin = !!(res.data && res.data.admin)
          if (!this.isAdmin) this.$message.warning('仅系统管理员可访问「申请配置」')
        }
      })
      this.loadTargets()
      this.loadOrg()
      this.loadGrants()
    },
    loadTargets() {
      grantTargets().then(res => {
        if (res && res.code === 200) {
          this.targets = res.data || { categories: [], subCategories: [], flows: [] }
        }
      })
    },
    loadOrg() {
      grantOrgTree().then(res => {
        if (res && res.code === 200) this.orgTree = res.data || { centers: [], departments: [] }
      })
      getUserList({ page: 1, limit: 2000, isValid: 1 }).then(res => {
        if (res && res.code === 200 && res.data) this.users = res.data.records || res.data || []
      })
      getTRoleSelectByPage({ page: 1, limit: 999, roleName: '' }).then(res => {
        if (res && res.code === 200) this.roles = res.data || []
      }).catch(() => {})
    },
    loadGrants() {
      this.grantLoading = true
      const f = this.listFilter
      const params = {}
      if (f.targetType != null) params.targetType = f.targetType
      if (f.targetValue) params.targetValue = f.targetValue
      if (f.subjectType != null) params.subjectType = f.subjectType
      if (f.subjectValue) params.subjectValue = f.subjectValue
      listGrants(params).then(res => {
        if (res && res.code === 200) this.grants = res.data || []
      }).finally(() => { this.grantLoading = false })
    },
    // 列表筛选：切换"对象类型"→ 清空"具体对象"并查询
    onListTargetTypeChange() {
      this.listFilter.targetValue = ''
      this.loadGrants()
    },
    // 列表筛选：切换"主体类型"→ 清空"具体主体"并查询
    onListSubjectTypeChange() {
      this.listFilter.subjectValue = ''
      this.loadGrants()
    },
    onTargetTypeChange() {
      this.grant.targetValue = ''
    },
    doSaveGrant() {
      if (this.grant.targetValue === '' || this.grant.targetValue == null) {
        this.$message.warning('请选择授权对象'); return
      }
      if (!this.grant.subjectValues.length) {
        this.$message.warning('请选择授权主体'); return
      }
      const names = this.grant.subjectValues.map(v => this.subjectName(v))
      saveGrants({
        targetType: this.grant.targetType,
        targetValue: String(this.grant.targetValue),
        targetName: this.targetName(this.grant.targetValue),
        subjectType: this.grant.subjectType,
        subjectValues: this.grant.subjectValues,
        subjectNames: names
      }).then(res => {
        if (res && res.code === 200) {
          this.$message.success('授权成功')
          this.grant.subjectValues = []
          this.loadGrants()
        }
      })
    },
    doDeleteGrant(row) {
      this.$confirm('确认删除该授权？删除后相应人员将不可见该流程（未再授权时）。', '提示', { type: 'warning' }).then(() => {
        deleteGrant({ id: row.id }).then(res => {
          if (res && res.code === 200) {
            this.$message.success('已删除')
            this.loadGrants()
          }
        })
      }).catch(() => {})
    },
    targetName(value) {
      const v = String(value)
      if (this.grant.targetType === 3) {
        const f = (this.targets.flows || []).find(x => x.flowKey === v)
        return f ? f.flowName : v
      }
      return v
    },
    subjectName(value) {
      const v = String(value)
      if (this.grant.subjectType === 1) {
        const c = this.orgTree.centers.find(x => String(x.id) === v); return c ? c.name : v
      }
      if (this.grant.subjectType === 2) {
        const d = this.orgTree.departments.find(x => String(x.id) === v); return d ? d.name : v
      }
      if (this.grant.subjectType === 3) {
        const u = this.users.find(x => String(x.id) === v); return u ? (u.trueName || u.userName) : v
      }
      const r = this.roles.find(x => String(x.id) === v); return r ? r.roleName : v
    },
    targetTypeText(t) { return t === 1 ? '一级分类' : t === 2 ? '二级分类' : '流程' },
    targetTagType(t) { return t === 3 ? 'success' : t === 2 ? 'warning' : '' },
    subjectTypeText(t) { return t === 1 ? '中心' : t === 2 ? '部门' : t === 3 ? '员工' : '角色' },
    iconUrlOf(f) { return fullIconUrl(f.iconUrl, this.baseApi) },
    // 图标上传
    beforeIconUpload(file) {
      const isPng = /\.png$/i.test(file.name)
      const lt2m = file.size / 1024 / 1024 < 2
      if (!isPng) { this.$message.error('仅支持上传 .png 图标文件'); return false }
      if (!lt2m) { this.$message.error('图标大小不能超过 2MB'); return false }
      return true
    },
    customIconUpload(opt, flow) {
      const fd = new FormData()
      fd.append('file', opt.file)
      request({ url: this.uploadAction, method: 'post', data: fd }).then(res => {
        const url = res && res.data && res.data.url
        if (!url) { this.$message.error('上传失败或返回缺少文件地址'); return }
        saveFlowIcon({ flowId: flow.id, iconUrl: url }).then(r => {
          if (r && r.code === 200) {
            flow.iconUrl = url
            this.$message.success('图标已更新')
          }
        })
      }).catch(() => this.$message.error('上传失败'))
    },
    resetIcon(flow) {
      saveFlowIcon({ flowId: flow.id, iconUrl: '' }).then(res => {
        if (res && res.code === 200) {
          flow.iconUrl = ''
          this.$message.success('已恢复默认图标')
        }
      })
    },
    onTabClick() {
      if (this.activeTab === 'icon') this.loadTargets()
    }
  }
}
</script>

<style scoped>
.wf-config { padding: 4px; }
.wf-config-card { max-width: 1080px; }
.wf-config-card >>> .el-card__body { padding: 20px 24px; }
.wf-config-head { display: flex; align-items: baseline; gap: 12px; }
.wf-config-title { font-size: 16px; font-weight: 600; color: #303133; }
.wf-config-tip { font-size: 12px; color: #909399; }
.wf-alert { margin-bottom: 16px; }

.wf-grant-form { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.wf-grant-col { background: #f8faff; border: 1px solid #e6eeff; border-radius: 8px; padding: 16px; }
.wf-col-title { font-size: 13px; color: #303133; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; }
.wf-step { display: inline-flex; width: 18px; height: 18px; border-radius: 50%; background: #2f6bff; color: #fff; font-size: 12px; align-items: center; justify-content: center; margin-right: 8px; }
.wf-radio { margin-bottom: 12px; }
.wf-select { width: 100%; }
.wf-grant-actions { margin-top: 16px; display: flex; align-items: center; gap: 12px; }
.wf-grant-hint { font-size: 12px; color: #909399; }

.wf-list-toolbar { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
.wf-cell-name { margin-left: 8px; color: #303133; }

.wf-icon-search { margin-bottom: 16px; }
.wf-icon-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
.wf-icon-card { border: 1px solid #ebeef5; border-radius: 10px; padding: 16px; text-align: center; transition: all .2s; background: #fff; }
.wf-icon-card:hover { border-color: #b9cdff; box-shadow: 0 6px 16px rgba(47, 107, 255, .12); transform: translateY(-2px); }
.wf-icon-preview { height: 48px; display: flex; align-items: center; justify-content: center; margin-bottom: 10px; }
.wf-icon-img { max-width: 40px; max-height: 40px; }
.wf-icon-ph { width: 40px; height: 40px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 20px; }
.wf-icon-name { font-size: 14px; color: #303133; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wf-icon-cat { font-size: 12px; color: #909399; margin-top: 4px; min-height: 16px; }
.wf-icon-ops { margin-top: 10px; display: flex; align-items: center; justify-content: center; gap: 8px; }
.wf-icon-ops .el-upload { display: inline-flex; }
.wf-icon-empty { grid-column: 1 / -1; text-align: center; color: #909399; padding: 32px 0; }
</style>
